/*
License

Copyright (c) 2022 Chukwubuikem Ume-Ugwa

Permission is hereby witheld, any person obtaining a copy
of this software and associated documentation files (the "Software"), cannot
use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software. These restrictions can be only lifted with
a written permission from Chukwubuikem Ume-Ugwa.
*/
import {
  Button,
  useDisclosure,
  useToast,
  Box,
  Container,
  Alert,
  AlertIcon,
  AlertDescription,
} from '@chakra-ui/react';
import QueueCreateModal from './create.queue.component';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Schemas from '../api/schemas';
import { lordWallet } from '..';
import { firebaseFacade } from '../api/firebase';
import { useWallet } from '@solana/wallet-adapter-react';
import { HorizontalScroll } from './horizontalscroll.component';
import { NewQueueComponent } from './queue.component';
import { log } from '../utils';
import { GeoPositionWatcher } from '../api/geopositioning';

async function queryGeolocationPermission(onPermissionResult) {
  navigator.permissions.query({ name: 'geolocation' }).then(result => {
    onPermissionResult(result.state);
  });
}

export default function Home(props) {
  const { streamingqAddress, isPlaying } = props;
  const [queues, setQueues] = useState({});
  const { isOpen, onOpen, onClose } = useDisclosure();

  const { publicKey } = useWallet();
  const { queue_schema } = Schemas;
  const [topCollabQueues, setTopCollabQueues] = useState([]);

  const [topGrossingQueues, setTopGrossingQueues] = useState([]);
  const [topTrendingQueues, setTopTrendingQueues] = useState([]);
  const [topSubsQueues, setTopSubsQueues] = useState([]);

  const [geoPositionEnabled, setGeoPositionEnabled] = useState(true);
  const [queueWatchers, setQueueWatchers] = useState({});
  const toast = useToast();

  const mergeQueue = (oldQueue, newQueue) => {
    newQueue.forEach(queue => {
      oldQueue[queue.queue_address] = queue;
    });
    setQueues({ ...oldQueue });
  };

  const onRemove = queueAddress => {
    const queue = queues[queueAddress];
    if (publicKey && publicKey.equals(lordWallet)) {
      firebaseFacade.deleteQueue(queue.queue_address);
    } else if (
      !publicKey ||
      publicKey.toBase58() !== queue.owner_wallet_address
    ) {
      toast({
        title: 'Unauthorized Action',
        description: 'You do not have authority over this queue!',
        duration: 5000,
        isClosable: true,
        status: 'error',
        position: 'top',
      });
    } else {
      firebaseFacade.deleteQueue(queue.queue_address);
    }
  };

  const handleGeopositionError = error => {
    const { code } = error;
    setGeoPositionEnabled(code !== 1);
    if (code === 1) {
      toast({
        title: 'Enable Location Access',
        description: 'OhOh! Location services is not enabled',
        duration: 5000,
        isClosable: true,
        status: 'error',
        position: 'top',
      });
    }
  };

  const handleTurnOnGeoPositioning = () => {
    navigator.geolocation.getCurrentPosition(
      () => setGeoPositionEnabled(true),
      handleGeopositionError,
      { timeout: 5000 }
    );
  };

  const transformedQueues = useMemo(
    () =>
      Object.values(queues).map(queue => ({
        ...props,
        queue: queue,
        onRemove: onRemove,
      })),
    [queues, isPlaying, streamingqAddress]
  );

  const transformQueues = useCallback(
    queuesWithStat =>
      queuesWithStat.map(stats => ({
        ...props,
        queue: stats.queue,
        queueStats: stats.stat,
        onRemove: onRemove,
      })),
    [isPlaying, streamingqAddress]
  );

  const handleQueueAdd = () => {
    onOpen();
  };

  const handleGeowatcherPositionChangeEvent = (distance, queue_address) => {
    const queue = queues[queue_address];
    if (queue !== undefined) {
      const { radius } = queue;
      log({
        component: 'Home',
        msg: 'Received geoposition event',
        distance: distance,
        queue_address: queue_address,
        radius: radius,
      });
      if (distance > radius) {
        log({
          component: 'Home',
          msg: 'Received geoposition event - removing queue',
          distance: distance,
          queue_address: queue_address,
          radius: radius,
          queues: queues
        });

        let editQueues = queues;
        delete editQueues[queue_address];
        delete queueWatchers[queue_address];

        setQueueWatchers({...queueWatchers})
        setQueues({ ...editQueues });
        log({
          component: 'Home',
          msg: 'Received geoposition event - removed queue',
          distance: distance,
          queue_address: queue_address,
          radius: radius,
          queues: editQueues
        });
      }
    }
  };

  useEffect(() => {
    return firebaseFacade.loadPublicQueues(_queues =>
      mergeQueue(queues, _queues)
    );
  }, []);

  useEffect(() => {
    return firebaseFacade.loadPrivateQueues(
      _queues => mergeQueue(queues, _queues),
      publicKey ? publicKey.toBase58() : 'Mistery'
    );
  }, [publicKey]);

  useEffect(() => {
    if (geoPositionEnabled) {
      const unsubscribe = firebaseFacade.loadGeoFencedQueues(_queues => {
        if (geoPositionEnabled) {
          mergeQueue(queues, _queues);
          setQueueWatchers(watchers => {
            _queues.forEach(queue => {
              watchers[queue.queue_address] = new GeoPositionWatcher(
                queue.queue_address,
                queue.geo_reference,
                navigator,
                handleGeowatcherPositionChangeEvent
              );
              log({
                component: 'Home',
                msg: 'Added watcher for queue',
                queue: queue,
              });
            });
            return { ...watchers };
          });
        }
      });

      return function () {
        unsubscribe();
        Object.values(queueWatchers).forEach(watcher => {
          try {
            watcher.stopWatch();
          } catch (error) {}
        });
      };
    }
  }, [geoPositionEnabled]);

  useEffect(() => {
    setTimeout(() => {
      queryGeolocationPermission(permission => {
        log({ component: 'Home', permission: permission });
        setGeoPositionEnabled(permission === 'granted');
      });
    }, 100);
  }, []);

  useEffect(() => {
    if (topTrendingQueues.length === 0) {
      firebaseFacade
        .getMonthlyTrendingQueue()
        .then(queues => transformQueues(queues))
        .then(setTopTrendingQueues);
    } else {
      setTopTrendingQueues(transformQueues(topTrendingQueues));
    }

    if (topGrossingQueues.length === 0) {
      firebaseFacade
        .getMonthlyTopGrossingQueue()
        .then(queues => transformQueues(queues))
        .then(setTopGrossingQueues);
    } else {
      setTopGrossingQueues(transformQueues(topGrossingQueues));
    }

    if (topSubsQueues.length === 0) {
      firebaseFacade
        .getMonthlyTopSubsQueue()
        .then(queues => transformQueues(queues))
        .then(setTopSubsQueues);
    } else {
      setTopSubsQueues(transformQueues(topSubsQueues));
    }

    if (topCollabQueues.length === 0) {
      firebaseFacade
        .getMonthlyTopCollabQueue()
        .then(queues => transformQueues(queues))
        .then(setTopCollabQueues);
    } else {
      setTopCollabQueues(transformQueues(topCollabQueues));
    }
  }, [isPlaying, streamingqAddress]);

  log({ component: 'Home', streamingqAddress: streamingqAddress });
  return (
    <>
      <Box>
        <Container>
          {!geoPositionEnabled && (
            <Alert status="info" margin={'8px'}>
              <AlertIcon />
              <AlertDescription textAlign="center">
                Turn on geopositioning to see queues only available in your
                locale!
              </AlertDescription>
              <Button
                onClick={handleTurnOnGeoPositioning}
                m={'8px'}
                p={'24px'}
                backgroundColor={'yellow.400'}
              >
                Turn on
              </Button>
            </Alert>
          )}
        </Container>
        <div>
          <HorizontalScroll
            Item={NewQueueComponent}
            header={null}
            data={transformedQueues}
            idKey={queue_schema.queue_address}
          />
          <HorizontalScroll
            Item={NewQueueComponent}
            header={'Popular this Month'}
            data={topTrendingQueues}
            idKey={queue_schema.queue_address}
          />
          <HorizontalScroll
            Item={NewQueueComponent}
            header={'Hot this Month'}
            data={topSubsQueues}
            idKey={queue_schema.queue_address}
          />
          <HorizontalScroll
            Item={NewQueueComponent}
            header={'Collaborative'}
            data={topCollabQueues}
            idKey={queue_schema.queue_address}
          />
          <Box height={'55px'}></Box>
          <Button
            shadow={'dark-lg'}
            borderRadius="50%"
            height={['40px', '60px']}
            width={['40px', '60px']}
            onClick={handleQueueAdd}
            position="fixed"
            bottom="100px"
            right={[8, 16, 32, 64, 128, 256]}
            bg={'white'}
            color={'black'}
            zIndex={1}
          >
            <span class="material-icons">add</span>
          </Button>
          <QueueCreateModal isOpen={isOpen} onClose={onClose} />
        </div>
      </Box>
    </>
  );
}
